/**
 * Amplify configuration for the admin site (admin.brokeornot.com) — quick-260909-k4v.
 *
 * Same Cognito user pool and same app client as the main SPA — there is no second pool and
 * no second client. The ONLY difference from src/lib/amplify.ts is the OAuth redirect list,
 * and that difference is the whole reason this file exists rather than importing that one.
 *
 * Why the list matters: Amplify v6 picks its `redirect_uri` by finding the first entry that
 * startsWith `window.location.origin + window.location.pathname`, and FALLS BACK TO THE
 * FIRST ENTRY when nothing matches. src/lib/amplify.ts lists the main app's origins, whose
 * first entry is http://localhost:5173/login — so reusing it from the admin origin would
 * silently send the user to the main app after a successful Google sign-in. A single-entry
 * list scoped to this origin cannot mis-select.
 *
 * The redirect is a /login URL, not the site root, because Cognito matches the callback
 * exactly against the app client's registered list and every entry there is a /login URL
 * (see EnvConfig.oauthRedirects in packages/infra/bin/broke-or-not.ts). The admin app has
 * no router — the dev server and, later, CloudFront both serve the same shell for /login,
 * where Amplify's OAuth listener completes the code exchange. See main.tsx.
 *
 * This deliberately does NOT import the main app's Capacitor secure-storage token
 * provider: the admin site is web-only, so default storage is correct and there is no
 * native shell to serve.
 */
import { Amplify } from 'aws-amplify';
// REQUIRED for the Google callback to complete in a PRODUCTION build. The OAuth
// code→token exchange runs from a listener Amplify registers only as an import side-effect
// of signInWithRedirect, which lives in the Authenticator's chunk. On /login?code=…,
// fetchAuthSession() blocks until that exchange completes — so if the listener is not
// already registered at boot, nothing ever starts it and the page hangs blank. The main
// app hit exactly this (debug session google-sso-login-blank, 2026-07-09); importing it
// here in the entry chunk is Amplify's documented fix for code-split apps. Dev is
// unaffected either way, which is precisely why it only shows up once deployed.
import 'aws-amplify/auth/enable-oauth-listener';

/**
 * The one origin this build is served from, as a full /login callback URL.
 *
 * Supplied at build time so a single source file serves local dev (localhost:5174) and, in
 * future, the deployed admin host. Whatever value is used here MUST also be listed in that
 * environment's EnvConfig.oauthRedirects, or Cognito rejects the callback with
 * redirect_mismatch.
 */
const oauthRedirect =
  (import.meta.env.VITE_ADMIN_OAUTH_REDIRECT as string) || 'http://localhost:5174/login';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID as string,
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID as string,
      // identityPoolId intentionally omitted — not needed for SPA + API Gateway JWT auth.
      loginWith: {
        oauth: {
          domain:
            (import.meta.env.VITE_OAUTH_DOMAIN as string) || 'auth.sandbox.app.brokeornot.com',
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: [oauthRedirect],
          redirectSignOut: [oauthRedirect],
          responseType: 'code',
        },
      },
    },
  },
});
